import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import {
  login as loginService,
  loginPhonepe as loginPhonepeService,
  ssoLogin,
  verifyOtp as verifyOtpService,
  verifyEmailOTP as verifyEmailOTPService,
  submitUserDetails,
  userInfo as userInfoService,
  getConsultationSummary as getConsultationSummaryService,
  cancelSlotRequest,
  appConfig,
  rescheduleSlotRequest,
  loginMagicLink,
  refreshAuthToken,
  authLogout,
  getPrebookedProcesses,
} from '../services';
import scanAndPaySaga from './scanAndPay';
import { store } from '../store/index';
import { updateLoginState, setAppConfig, updateIframeFlag } from '../actions';
import { showError, tabFormatter, pbDetailFormatter } from '../utils';
import { logEvent } from '../utils/logEvent';
import config from '../config';

/**
 * Login
 */
export function* login({ payload }) {
  try {
    console.log('login saga called', payload);
    const queryParams = new URLSearchParams(payload.queryParams);
    let platform;
    if (queryParams.get('platform')) {
      platform = queryParams.get('platform');
    }
    yield call(
      loginService,
      payload.phoneNumber,
      payload.countryCode,
      payload.encData,
      payload.resend,
      platform,
      payload.ssoInfo,
      payload.storedUTMParams,
      payload.emailCode,
      payload.emailVerificationId,
      payload.channel
    );

    yield put({
      type: 'OTP_SENT',
      payload: {
        phoneNumber: payload.phoneNumber,
        countryCode: payload.countryCode,
      },
    });

    if (payload.reVerify) {
      yield call(payload.history.push, `/re-verify-otp${payload.queryParams}`);
    } else {
      yield call(payload.history.push, `/verify-otp${payload.queryParams}`);
    }
  } catch (err) {
    console.log(err);
    /* istanbul ignore next */
    yield put({
      type: 'LOGIN_FAILURE',
      payload: err,
    });
  }
}

export function* logout({ payload }) {
  try {
    const { history } = payload;
    const authToken = yield select((state) => state.user.authToken);
    if (authToken) {
      yield call(authLogout, authToken);
    }
    yield put({
      type: 'SET_INITIAL_STATE',
    });
    sessionStorage.removeItem('selected_address');
    yield put(updateLoginState('LOGGED_OUT'));
    history.replace('/');
  } catch (err) {
    console.log(err);
  }
}

export function* updateAppConfig(authToken) {
  const result = yield call(appConfig, authToken);
  yield put(setAppConfig(result.config));
}

export function* resetUser({ authToken }) {
  console.log(authToken);
  const userInfoResult = yield call(userInfoService, authToken);

  yield all([
    put({
      type: 'SET_USER_INFO',
      payload: userInfoResult,
    }),
    call(updateAppConfig, authToken),
  ]);
}

export function* loginPhonepe({ payload }) {
  try {
    const result = yield call(loginPhonepeService, payload.grantToken);

    yield put({
      type: 'AUTH_TOKEN',
      payload: result.headers.authorization,
    });

    // With HTTP-only cookies, refresh token is automatically managed by browser

    yield call(resetUser, result.headers.authorization);

    if (payload.queryParams) {
      // Check if there is a redirect query parameter
      const params = new URLSearchParams(payload.queryParams);
      if (params.get('rt')) {
        yield put(updateLoginState('LOGGED_IN'));
        let path = `/${params.get('rt')}`;
        if (params.get('params')) {
          path = `${path}${decodeURIComponent(params.get('params'))}`;
        }
        yield call(payload.history.push, path);
        return;
      }
    }

    switch (result.body.screen) {
      case 'oneLastStep': {
        yield put(updateLoginState('ONE_LAST_STEP'));
        yield call(payload.history.replace, '/one-last-step');
        break;
      }
      case 'home': {
        yield put(updateLoginState('LOGGED_IN'));
        yield call(payload.history.replace, '/home');
        break;
      }
      default: {
        yield call(payload.history.replace, '/home');
        break;
      }
    }
  } catch (err) {
    console.log(err);
    /* istanbul ignore next */
    yield put({
      type: 'LOGIN_FAILURE',
      payload: err,
    });
  }
}
function* moduleBasedLogin() {
  yield put({
    type: 'SET_MODULE_BASED_LOGIN',
    payload: true,
  });
  yield put(updateLoginState('LOGGED_IN'));
}
export function* loginSso({ payload }) {
  try {
    const res = yield call(
      ssoLogin,
      payload.encrypted,
      payload.clientId,
      payload.data,
      payload.ssoType
    );

    if (res.body.ssoLoginData) {
      const currentUserInfo = payload.userInfo;
      currentUserInfo.ssoInfo = res.body.ssoLoginData;
      console.log(currentUserInfo, 'set user State');
      yield put({
        type: 'SET_USER_INFO',
        payload: { userInfo: currentUserInfo },
      });
    }

    if (res.headers.authorization) {
      let finalAuthToken = res.headers.authorization;

      yield put({
        type: 'AUTH_TOKEN',
        payload: finalAuthToken,
      });

      // Only call refreshAuthToken if the feature flag is enabled
      // if (config.features?.enableRefreshToken) {
      //   const result = yield call(refreshAuthToken, finalAuthToken);
      //   finalAuthToken = result.headers.authorization;
      //   yield put({
      //     type: 'AUTH_TOKEN',
      //     payload: finalAuthToken,
      //   });
      // }

      const userInfoResult = yield call(userInfoService, finalAuthToken);
      logEvent('Sso Login Success', {
        userInfo: userInfoResult.userInfo,
        screen: res.body.screen,
      });
      userInfoResult.userInfo.ssoInfo = res.body.ssoLoginData;
      // Some SSO backends (e.g. abw) return the policy identifier as
      // `wellnessId` rather than `policyNumber`; fall back so the dope
      // get-relatives call has a policyNumber. `plan`/`relativeId` come from
      // the same response and drive relative fetching/selection downstream.
      userInfoResult.userInfo.policyNumber =
        res.body.policyNumber ?? res.body.wellnessId;
      userInfoResult.userInfo.plan =
        res.body.plan ?? userInfoResult.userInfo.plan;
      userInfoResult.userInfo.relativeId =
        res.body.relativeId ?? userInfoResult.userInfo.relativeId;
      yield all([
        put({
          type: 'SET_USER_INFO',
          payload: userInfoResult,
        }),
        call(updateAppConfig, finalAuthToken),
      ]);
      // Iframe mode can also arrive in the SSO response body, not just as an
      // `?iframe=1` query param (which SsoContainer already handles). Reading it
      // here removes the dependency on the host app building the URL correctly.
      // Guarded so a response without the field cannot clobber a flag that was
      // already set from the query param. Applies to every screen, so it sits
      // outside the switch below.
      if (res.body.iframe) {
        yield put(updateIframeFlag(res.body.iframe));
      }
      switch (res.body.screen) {
        case 'oneLastStep': {
          yield put(updateLoginState('ONE_LAST_STEP'));
          yield call(payload.history.replace, '/one-last-step', {
            reVerify: false,
          });
          break;
        }
        case 'home': {
          console.log('home screen');
          yield put(updateLoginState('LOGGED_IN'));
          yield call(payload.history.replace, '/home', {
            reVerify: false,
          });
          break;
        }
        case 'teleconsult': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/consultation/online/preview', {
            reVerify: false,
          });
          break;
        }
        case 'labs': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/lab-test', {
            reVerify: false,
          });
          break;
        }
        case 'pharmacy': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/pharmacy', {
            reVerify: false,
          });
          break;
        }
        case 'opd': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/consultation/preview', {
            reVerify: false,
          });
          break;
        }
        case 'hra': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/hra', {
            reVerify: false,
          });
          break;
        }
        case 'videosPodcasts': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/health-content/fitness-home', {
            reVerify: false,
          });
          break;
        }
        case 'healthCheckup':
        case 'universal-ahc': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/lab-test/universal-ahc', {
            reVerify: false,
          });
          break;
        }
        case 'reimbursement': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/reimbursement/preview', {
            reVerify: false,
          });
          break;
        }
        case 'vaccination': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/vaccination/preview', {
            reVerify: false,
          });
          break;
        }
        case 'dental': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/dental/preview', {
            reVerify: false,
          });
          break;
        }
        case 'gym': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/cult/select-plan', {
            reVerify: false,
          });
          break;
        }
        case 'vision': {
          yield* moduleBasedLogin();
          yield call(payload.history.replace, '/vision/preview', {
            reVerify: false,
          });
          break;
        }
        default: {
          yield call(payload.history.replace, '/login');
          break;
        }
      }
    } else {
      yield call(payload.history.replace, '/login');
      // Optional error from backend when sso does not have auth header
      if (res.body.errorMessage) {
        const err = new Error('Invalid response');
        err.response = {
          data: {
            errorHeader: res.body?.errorHeader,
            errorMessage: res.body.errorMessage,
          },
        };
        showError(err);
      }
    }
  } catch (err) {
    console.log(err);
    showError(err);
    /* istanbul ignore next */
    yield put({
      type: 'LOGIN_FAILURE',
      payload: err,
    });
  }
}

export function* loginWithToken({ payload }) {
  try {
    let finalAuthToken = payload.authorization;

    yield put({
      type: 'AUTH_TOKEN',
      payload: finalAuthToken,
    });

    // if (config.features?.enableRefreshToken) {
    //   const result = yield call(refreshAuthToken, finalAuthToken);
    //   finalAuthToken = result.headers.authorization;
    //   yield put({
    //     type: 'AUTH_TOKEN',
    //     payload: finalAuthToken,
    //   });
    // }

    const userInfoResult = yield call(userInfoService, finalAuthToken);

    yield all([
      put({
        type: 'SET_USER_INFO',
        payload: userInfoResult,
      }),
      call(updateAppConfig, finalAuthToken),
    ]);
    if (payload.queryParams) {
      // Check if there is a redirect query parameter
      const params = new URLSearchParams(payload.queryParams);
      if (params.get('rt')) {
        yield put(updateLoginState('LOGGED_IN'));
        let path = `/${params.get('rt')}`;
        if (params.get('params')) {
          path = `${path}${decodeURIComponent(params.get('params'))}`;
        }
        if (params.get('hidetabs')) {
          const hidetabsSeparator = path.includes('?') ? '&' : '?';
          path = `${path}${hidetabsSeparator}hidetabs=${decodeURIComponent(
            params.get('hidetabs')
          )}`;
          yield put({
            type: 'HIDE_NAVIGATOR_FOR_BOT',
            payload: true,
          });
        }

        // Carry over extra deeplink params (e.g. `cId`) passed as siblings of
        // `rt`. Skip auth/token keys so JWTs are never re-exposed in the URL.
        const CONTROL_KEYS = ['rt', 'params', 'hidetabs'];
        const SENSITIVE_KEYS = [
          'authtoken',
          'auth',
          'ott',
          'token',
          'refreshtoken',
        ];
        params?.forEach((value, key) => {
          const lowerKey = key.toLowerCase();
          if (
            CONTROL_KEYS.includes(lowerKey) ||
            SENSITIVE_KEYS.includes(lowerKey)
          ) {
            return;
          }
          const separator = path.includes('?') ? '&' : '?';
          path = `${path}${separator}${key}=${encodeURIComponent(value)}`;
        });

        yield call(payload.history.push, path);
        return;
      }
    }
  } catch (err) {
    console.log(err);
    /* istanbul ignore next */
    yield put({
      type: 'LOGIN_FAILURE',
      payload: err,
    });
  }
}

export function* loginMagicCode({ payload }) {
  try {
    const response = yield call(
      loginMagicLink,
      payload.magicLink,
      payload.platform
    );
    let finalAuthToken = response.headers.authorization;

    // Only call refreshAuthToken if the feature flag is enabled
    // if (config.features?.enableRefreshToken) {
    //   const res = yield call(refreshAuthToken, finalAuthToken);
    //   finalAuthToken = res.headers.authorization;
    // }

    if (response.body.screen !== 'login') {
      yield put({
        type: 'AUTH_TOKEN',
        payload: finalAuthToken,
      });

      const userInfoResult = yield call(userInfoService, finalAuthToken);

      yield all([
        put({
          type: 'SET_USER_INFO',
          payload: userInfoResult,
        }),
        call(updateAppConfig, finalAuthToken),
      ]);
    }

    switch (response.body.screen) {
      case 'oneLastStep': {
        yield put(updateLoginState('ONE_LAST_STEP'));
        // if (payload.tab) {
        const tab = tabFormatter(payload.tab);
        // } else {
        yield call(payload.history.push, {
          pathname: '/one-last-step',
          search:
            tab && payload.tab
              ? `?rt=${encodeURIComponent(payload.tab)}`
              : response.body.bookingInfo
                ? `?rt=${encodeURIComponent(
                    'home/consult'
                  )}&params=${encodeURIComponent(
                    '?consultationId=' +
                      response.body.bookingInfo.consultationId
                  )}`
                : undefined,
          state: {
            encData: response.body.encData,
          },
        });
        break;
      }
      case 'consult': {
        yield put(updateLoginState('LOGGED_IN'));
        yield call(payload.history.push, {
          pathname: '/home/consult',
          search: response.body.bookingInfo
            ? '?consultationId=' + response.body.bookingInfo.consultationId
            : undefined,
          state: {
            encData: response.body.encData,
          },
        });
        break;
      }
      case 'care': {
        yield put(updateLoginState('LOGGED_IN'));
        yield call(payload.history.push, '/home/consult');
        break;
      }
      case 'home': {
        yield put(updateLoginState('LOGGED_IN'));
        if (payload.tab) {
          yield put({
            type: 'SET_MAGIC_BASED_LOGIN',
            payload: true,
          });
          const detail =
            payload.platform === 'PB' && payload.sourceId
              ? pbDetailFormatter(payload.tab, payload.sourceId)
              : null;
          if (detail) {
            yield call(payload.history.push, detail.pathname, detail.state);
          } else {
            const tab = tabFormatter(payload.tab);
            if (tab) {
              const conciergeBookingInfo = response.body?.conciergeBookingInfo;
              yield call(payload.history.push, tab, {
                isConciergeBooking: conciergeBookingInfo?.isConciergeBooking,
                cartId: conciergeBookingInfo?.cartId,
                paymentNext: conciergeBookingInfo?.paymentNext,
                userConciergePayment:
                  conciergeBookingInfo?.userConciergePayment,
              });
            }
          }
        } else {
          yield call(payload.history.replace, '/home');
        }
        break;
      }
      case 'login': {
        yield call(payload.history.push, {
          pathname: '/login',
          search: response.body.bookingInfo
            ? `? rt = ${encodeURIComponent(
                'home/consult'
              )} & params=${encodeURIComponent(
                '?consultationId=' + response.body.bookingInfo.consultationId
              )} `
            : undefined,
          state: {
            encData: response.body.encData,
          },
        });
        break;
      }
      default: {
        yield call(payload.history.push, '/login');
        break;
      }
    }
  } catch (err) {
    console.log(err);
  }
}

export function* verifyOtp({ payload }) {
  console.log('vrifyOTP payload', payload);
  try {
    const queryParams = new URLSearchParams(payload.queryParams);
    let platform;
    if (queryParams.get('platform')) {
      platform = queryParams.get('platform');
    }
    const result = yield call(
      verifyOtpService,
      payload.phoneNumber,
      payload.otp,
      platform,
      payload.ssoInfo,
      payload.emailCode
    );
    if (result?.body?.message === 'otpVerified') {
      const hexCode = sessionStorage.getItem('scanAndPayHexCode');
      logEvent('Onboarding OTP Verified', hexCode ? { hexCode } : undefined);
      if (hexCode) {
        sessionStorage.removeItem('scanAndPayHexCode');
      }
    }

    console.log('verifyOtp', result);

    yield put({
      type: 'AUTH_TOKEN',
      payload: result.headers.authorization,
    });

    yield put({
      type: 'DONT_SHOW_NAVIGATOR',
      payload: false,
    });

    const userInfoResult = yield call(
      userInfoService,
      result.headers.authorization
    );

    yield all([
      put({
        type: 'SET_USER_INFO',
        payload: userInfoResult,
      }),
      call(updateAppConfig, result.headers.authorization),
    ]);

    if (payload.queryParams) {
      // Check if there is a redirect query parameter
      const params = new URLSearchParams(payload.queryParams);
      if (params.get('rt')) {
        if (
          params.get('rt').indexOf('buying-journey') !== -1 &&
          (result.body.screen === 'benefitsOption' ||
            result.body.screen === 'oneLastStep')
        ) {
          yield put(updateLoginState('ONE_LAST_STEP'));
          let path = `/one-last-step`;
          if (params.get('rt')) {
            path = `${path}?rt=${params.get('rt')}`;
          }
          yield call(payload.history.push, path);
          return;
        } else {
          yield put(updateLoginState('LOGGED_IN'));
          let path = `/${params.get('rt')}`;
          if (params.get('params')) {
            path = `${path}${decodeURIComponent(params.get('params'))}`;
          }
          yield call(payload.history.push, path, {
            from: 'rt',
          });
          return;
        }
      }
    }

    yield put({
      type: 'TOGGLE_PRESCRIPTION_POPUP',
      payload: true,
    });

    switch (result.body.screen) {
      case 'opdActivated': {
        const policyDetails = result.body.policy;
        yield call(payload.history.push, '/opd-activated', policyDetails);
        break;
      }
      case 'oneLastStep': {
        yield put(updateLoginState('ONE_LAST_STEP'));
        yield call(payload.history.replace, '/one-last-step');
        break;
      }
      case 'benefitsOption': {
        yield put(updateLoginState('SELECT_USER'));
        yield call(payload.history.push, '/select-user');
        break;
      }
      case 'sponsoredEmail': {
        yield put(updateLoginState('SPONSOR_EMAIL'));
        yield call(payload.history.push, '/corporate-onboard');
        break;
      }
      case 'home': {
        yield put(updateLoginState('LOGGED_IN'));
        yield call(payload.history.replace, '/home');
        break;
      }
      case 'ssoAuthLoginPage': {
        yield put(updateLoginState('LOGGED_IN'));
        const ssoLoginUrl = result.body.ssoLoginUrl;
        window.location.href = ssoLoginUrl;
        break;
      }
      default: {
        yield call(payload.history.replace, '/home');
        break;
      }
    }
  } catch (err) {
    console.log(err);
    showError(err);
  }
}

export function* verifyEmailOTP({ payload }) {
  console.log('verifyOTP payload', payload);
  try {
    const queryParams = new URLSearchParams(payload.queryParams);
    let platform;
    if (queryParams.get('platform')) {
      platform = queryParams.get('platform');
    }
    const result = yield call(
      verifyEmailOTPService,
      payload.email,
      payload.otp,
      platform
    );

    console.log('verifyOtp', result);

    yield put({
      type: 'DONT_SHOW_NAVIGATOR',
      payload: false,
    });

    if (payload.queryParams) {
      // Check if there is a redirect query parameter
      const params = new URLSearchParams(payload.queryParams);
      if (params.get('rt')) {
        if (
          params.get('rt').indexOf('buying-journey') !== -1 &&
          (result.body.screen === 'benefitsOption' ||
            result.body.screen === 'oneLastStep')
        ) {
          yield put(updateLoginState('ONE_LAST_STEP'));
          let path = `/one-last-step`;
          if (params.get('rt')) {
            path = `${path}?rt=${params.get('rt')}`;
          }
          yield call(payload.history.push, path);
          return;
        } else {
          yield put(updateLoginState('LOGGED_IN'));
          let path = `/${params.get('rt')}`;
          if (params.get('params')) {
            path = `${path}${decodeURIComponent(params.get('params'))}`;
          }
          yield call(payload.history.push, path, {
            from: 'rt',
          });
          return;
        }
      }
    }

    yield put({
      type: 'TOGGLE_PRESCRIPTION_POPUP',
      payload: true,
    });

    switch (result.body.screen) {
      case 'oneLastStep': {
        yield put(updateLoginState('ONE_LAST_STEP'));
        yield call(payload.history.replace, '/one-last-step');
        break;
      }
      case 'home': {
        yield put(updateLoginState('LOGGED_IN'));
        yield call(payload.history.replace, '/home');
        break;
      }
      default: {
        yield call(payload.history.replace, '/home');
        break;
      }
    }
  } catch (err) {
    console.log(err);
    showError(err);
  }
}

export function* submitUserInfo({ payload }) {
  try {
    const { user } = store.getState();
    yield call(
      submitUserDetails,
      payload.name,
      payload.dob,
      payload.gender,
      user.authToken
    );

    const userInfoResult = yield call(userInfoService, user.authToken);

    yield put({
      type: 'SET_USER_INFO',
      payload: userInfoResult,
    });

    yield all([
      put({
        type: 'SET_USER_INFO',
        payload: userInfoResult,
      }),
      call(updateAppConfig, user.authToken),
    ]);
    yield put(updateLoginState('LOGGED_IN'));
    if (payload.history.location.search) {
      const queryParam = new URLSearchParams(payload.history.location.search);
      if (queryParam.get('rt')) {
        yield put(updateLoginState('LOGGED_IN'));
        let path = `/ ${queryParam.get('rt')} `;
        yield call(payload.history.push, path);
        return;
      }
    }
    if (payload.queryParams) {
      // Check if there is a redirect query parameter
      const params = new URLSearchParams(payload.queryParams);
      if (params.get('rt')) {
        yield put(updateLoginState('LOGGED_IN'));
        let path = `/ ${params.get('rt')} `;
        if (params.get('params')) {
          path = `${path}${decodeURIComponent(params.get('params'))} `;
        }
        yield call(payload.history.push, path);
        return;
      }
    }
    yield call(payload.history.replace, '/home');
  } catch (err) {
    console.log(err);
    /* istanbul ignore next */
    yield put({
      type: 'LOGIN_FAILURE',
      payload: err,
    });
  }
}

/**
 * Consultation Summary
 */
export function* getConsultationSummary() {
  try {
    const { user, chat } = store.getState();
    const response = yield call(
      getConsultationSummaryService,
      chat.consultationId,
      user.userId,
      user.authToken
    );

    yield put({
      type: 'SET_CONSULTATION_SUMMARY',
      payload: response,
    });
  } catch (err) {
    console.log(err);
  }
}

/**
 * Consultation Cancel Booking
 */
export function* cancelConsultationBooking({ payload }) {
  try {
    const { requestId, history } = payload;
    const { user } = store.getState();
    const response = yield call(cancelSlotRequest, requestId, user.authToken);
    if (response.message === 'success') {
      history.goBack();
    } else {
      console.log('Cancellation failed');
    }
  } catch (err) {
    console.log(err);
  }
}

/**
 * Consultation Reschedule Booking
 */
export function* rescheduleConsultationBooking({ payload }) {
  try {
    const { requestId, history } = payload;
    const { user } = store.getState();
    const response = yield call(
      rescheduleSlotRequest,
      requestId,
      user.authToken
    );
    if (response.message === 'success') {
      history.replace(`/ consultation / appointment / ${response.doctorId} `, {
        doctorName: payload.doctorName,
      });
    } else {
      console.log('Cancellation failed');
    }
  } catch (err) {
    console.log(err);
  }
}

export function* getPreBookedProcessesSaga({ payload }) {
  try {
    const { user } = store.getState();
    const response = yield call(getPrebookedProcesses, user.authToken);
    yield put({
      type: 'SET_PRE_BOOKED_PROCESSES',
      payload: response,
    });
    if (payload.callback) {
      payload.callback(response);
    }
  } catch (err) {
    console.log(err);
  }
}

/**
 * User Sagas
 */
export default function* root() {
  yield all([
    takeLatest('LOGIN', login),
    takeLatest('LOGOUT', logout),
    takeLatest('SSO_LOGIN', loginSso),
    takeLatest('LOGIN_WITH_TOKEN', loginWithToken),
    takeLatest('SSO_PHONEPE', loginPhonepe),
    takeLatest('SSO_MAGIC_CODE', loginMagicCode),
    takeLatest('VERIFY_OTP', verifyOtp),
    takeLatest('VERIFY_EMAIL_OTP', verifyEmailOTP),
    takeLatest('SUBMIT_USER_INFO', submitUserInfo),
    takeLatest('GET_CONSULTATION_SUMMARY', getConsultationSummary),
    takeLatest('CANCEL_CONSULTATION_BOOKING', cancelConsultationBooking),
    takeLatest(
      'RESCHEDULE_CONSULTATION_BOOKING',
      rescheduleConsultationBooking
    ),

    takeLatest('RESET_USER', resetUser),
    takeLatest('GET_PRE_BOOKED_PROCESSES', getPreBookedProcessesSaga),
  ]);
}
